import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { getBusinessToday } from "@/utils/businessTime";
import type { DeliveryStatus } from "@/services/delivery.service";

/** Date par défaut = aujourd'hui en heure Guyane (recalculée à chaque lecture). */
function getDefaultDate(): string {
  return getBusinessToday();
}
const STORAGE_KEY = "filters/deliveries";

export interface DeliveriesFiltersState {
  search: string;
  status: DeliveryStatus | "all";
  driver: string;
  zone: string;
  date: string;
}

const KEYS = {
  search: "search",
  status: "status",
  driver: "driver",
  zone: "zone",
  date: "date",
} as const;

function readParams(searchParams: URLSearchParams): DeliveriesFiltersState {
  return {
    search: searchParams.get(KEYS.search) ?? "",
    status: (searchParams.get(KEYS.status) as DeliveriesFiltersState["status"]) ?? "all",
    driver: searchParams.get(KEYS.driver) ?? "all",
    zone: searchParams.get(KEYS.zone) ?? "all",
    date: searchParams.get(KEYS.date) ?? getDefaultDate(),
  };
}

function toParams(state: DeliveriesFiltersState): Record<string, string> {
  const out: Record<string, string> = {};
  if (state.search) out[KEYS.search] = state.search;
  if (state.status && state.status !== "all") out[KEYS.status] = state.status;
  if (state.driver && state.driver !== "all") out[KEYS.driver] = state.driver;
  if (state.zone && state.zone !== "all") out[KEYS.zone] = state.zone;
  if (state.date && state.date !== getDefaultDate()) out[KEYS.date] = state.date;
  return out;
}

function loadPersisted(): DeliveriesFiltersState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DeliveriesFiltersState;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function savePersisted(state: DeliveriesFiltersState): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

/**
 * Hook pour les filtres livraisons (synchronisés avec l’URL, persistés en localStorage).
 */
export function useDeliveriesFilters(): {
  filters: DeliveriesFiltersState;
  setSearch: (v: string) => void;
  setStatus: (v: DeliveryStatus | "all") => void;
  setDriver: (v: string) => void;
  setZone: (v: string) => void;
  setDate: (v: string) => void;
  clearDate: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readParams(searchParams);
  const hasRestored = useRef(false);

  useEffect(() => {
    if (hasRestored.current) return;
    const fromUrl = readParams(searchParams);
    const hasParams =
      fromUrl.search ||
      (fromUrl.status && fromUrl.status !== "all") ||
      (fromUrl.driver && fromUrl.driver !== "all") ||
      (fromUrl.zone && fromUrl.zone !== "all") ||
      (fromUrl.date && fromUrl.date !== getDefaultDate());
    if (hasParams) {
      hasRestored.current = true;
      return;
    }
    const saved = loadPersisted();
    if (saved) {
      hasRestored.current = true;
      const params = new URLSearchParams(searchParams);
      Object.entries(toParams(saved)).forEach(([k, v]) => params.set(k, v));
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const update = useCallback(
    (patch: Partial<DeliveriesFiltersState>) => {
      const next = { ...readParams(searchParams), ...patch };
      savePersisted(next);
      const params = new URLSearchParams(searchParams);
      const added = toParams(next);
      [KEYS.search, KEYS.status, KEYS.driver, KEYS.zone, KEYS.date].forEach(
        (k) => params.delete(k),
      );
      Object.entries(added).forEach(([k, v]) => params.set(k, v));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return {
    filters,
    setSearch: (v) => update({ search: v }),
    setStatus: (v) => update({ status: v }),
    setDriver: (v) => update({ driver: v }),
    setZone: (v) => update({ zone: v }),
    setDate: (v) => update({ date: v }),
    clearDate: () => update({ date: getDefaultDate() }),
  };
}
